import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQueryClient } from '@tanstack/react-query'

import { useEscape } from '../hooks/useEscape'
import { keys } from '../hooks/queries'
import { abonnierePush, bannerAngetippt, type PushHinweis } from '../lib/tippziel'
import { XIcon } from './Icons'
import { useTexte } from '../texte'

/** How long the banner stays when nobody touches it -- about as long as the system's. */
const STEHT_MS = 8000

/**
 * The app's own banner for a push that arrives while the app is open (1.82.0).
 *
 * From use: with the app open on the map, a tap on the system banner left the
 * app where it was. Measured with 1.81.0: iOS fired no `notificationclick` at
 * all (a known WebKit fault for a home screen app that is already open), so
 * no road behind that event can carry. This banner is a tap in the page
 * itself and jumps through `bannerAngetippt()` -- the same `springeZu()` as
 * every other road. The worker closes the system notification once the app
 * has answered, so two banners do not stand on top of each other.
 *
 * Every push also refreshes the chat list and the open chat at once: the
 * message the push announces should be there when the banner is tapped, and
 * when its chat is already open there is no banner, only the message.
 *
 * At the body, like every layer on top (`createPortal`); its own Escape.
 */
export function PushBanner() {
  const t = useTexte().bausteine.pushbanner
  const client = useQueryClient()
  const [hinweis, setHinweis] = useState<PushHinweis | null>(null)

  useEffect(
    () =>
      abonnierePush((neu) => {
        void client.invalidateQueries({ queryKey: keys.threads })
        void client.invalidateQueries({ queryKey: ['messages'] })
        if (neu.zeigen) setHinweis(neu)
      }),
    [client],
  )

  useEffect(() => {
    if (hinweis === null) return
    const uhr = window.setTimeout(() => setHinweis(null), STEHT_MS)
    return () => window.clearTimeout(uhr)
  }, [hinweis])

  const zu = useCallback(() => setHinweis(null), [])
  useEscape(hinweis === null ? null : zu)

  if (hinweis === null) return null

  return createPortal(
    <div className="pushbanner" role="status" data-schluessel="pushbanner">
      <button
        type="button"
        className="pushbanner__oeffnen"
        aria-label={t.oeffnen(hinweis.titel)}
        onClick={() => {
          setHinweis(null)
          bannerAngetippt(hinweis.url)
        }}
      >
        <span className="pushbanner__titel">{hinweis.titel}</span>
        {hinweis.text !== '' && <span className="pushbanner__text">{hinweis.text}</span>}
      </button>
      <button type="button" className="pushbanner__weg" aria-label={t.schliessen} onClick={zu}>
        <XIcon size={14} />
      </button>
    </div>,
    document.body,
  )
}
